import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { UsersRound, Mail, Bell, BellOff, CheckCircle2, XCircle, Edit2, Key, Trash2 } from "lucide-react";

/**
 * UsersTable — Tabla de usuarios del sistema + diálogo de confirmación de borrado.
 *
 * Extraída 1:1 desde `pages/UsersPage.js`. Renderiza cada usuario (avatar, email, roles,
 * responsable, preferencias de notificación, fecha y estado) con acciones de fila: editar,
 * resetear contraseña (solo `auth_provider === "local"`), eliminar (guard RBAC, no a uno
 * mismo) y toggle de activo. Incluye el `AlertDialog` de confirmación de borrado.
 *
 * @param {Array}    props.users              Lista de usuarios a mostrar.
 * @param {Function} props.getInitials        (name) => string — iniciales para el avatar.
 * @param {Function} props.getRoleColor       (role) => string — clases de color del badge de rol.
 * @param {Function} props.getRoleLabel       (role) => string — etiqueta legible del rol.
 * @param {Function} props.formatDate         (dateStr) => string — formatea fechas.
 * @param {Function} props.canEdit            (resource) => boolean — permiso de edición.
 * @param {Function} props.canDelete          (resource) => boolean — permiso de borrado.
 * @param {Object}   props.currentUser        Usuario autenticado (para evitar auto-borrado).
 * @param {string|null} props.deleting        Id del usuario en proceso de borrado (spinner).
 * @param {Function} props.openEditModal      (user) => void — abre el modal de edición.
 * @param {Function} props.openPasswordModal  (user) => void — abre el modal de reseteo de contraseña.
 * @param {Function} props.handleDeleteUser   (id, name) => void — inicia el flujo de borrado.
 * @param {Function} props.handleToggleActive (id, isActive) => void — activa/desactiva el usuario.
 * @param {boolean}  props.deleteDialogOpen   Apertura del diálogo de borrado.
 * @param {Function} props.setDeleteDialogOpen Setter del flag del diálogo de borrado.
 * @param {Object|null} props.userToDelete    Usuario pendiente de borrado (para el texto).
 * @param {Function} props.confirmDeleteUser  Confirma el borrado permanente.
 */
export const UsersTable = ({
  users,
  getInitials,
  getRoleColor,
  getRoleLabel,
  formatDate,
  canEdit,
  canDelete,
  currentUser,
  deleting,
  openEditModal,
  openPasswordModal,
  handleDeleteUser,
  handleToggleActive,
  deleteDialogOpen,
  setDeleteDialogOpen,
  userToDelete,
  confirmDeleteUser,
}) => {
  return (
    <>
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <UsersRound className="h-5 w-5 text-[#03BEE4]" />
            Usuarios del Sistema
          </CardTitle>
          <CardDescription className="text-slate-400">
            {users.length} usuarios registrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E1E5F]">
                  <TableHead className="text-slate-400">Usuario</TableHead>
                  <TableHead className="text-slate-400">Email</TableHead>
                  <TableHead className="text-slate-400">Roles</TableHead>
                  <TableHead className="text-slate-400">Responsable</TableHead>
                  <TableHead className="text-slate-400">Notificaciones</TableHead>
                  <TableHead className="text-slate-400">Fecha Registro</TableHead>
                  <TableHead className="text-slate-400">Estado</TableHead>
                  <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 bg-[#1B1B4B]">
                          <AvatarFallback className="bg-[#42E695] text-[#04042C] font-semibold">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-white font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles?.map((role) => (
                          <Badge
                            key={role}
                            className={`${getRoleColor(role)} text-white border-none text-xs`}
                          >
                            {getRoleLabel(role)}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.responsable_nombre ? (
                        <span className="text-slate-300 text-sm">{user.responsable_nombre}</span>
                      ) : (
                        <span className="text-slate-500 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {user.notification_email !== false ? (
                          <Badge className="bg-[#03BEE4]/20 text-[#03BEE4] text-xs">
                            <Mail className="h-3 w-3 mr-1" />
                            Email
                          </Badge>
                        ) : null}
                        {user.notification_inapp !== false ? (
                          <Badge className="bg-[#42E695]/20 text-[#42E695] text-xs">
                            <Bell className="h-3 w-3 mr-1" />
                            App
                          </Badge>
                        ) : null}
                        {user.notification_email === false && user.notification_inapp === false && (
                          <Badge className="bg-slate-500/20 text-slate-400 text-xs">
                            <BellOff className="h-3 w-3 mr-1" />
                            Sin notif.
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-300">{formatDate(user.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      {user.is_active !== false ? (
                        <Badge className="bg-[#42E695]/20 text-[#42E695] border-[#42E695]/30">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Activo
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactivo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {canEdit("users") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(user)}
                            className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                            data-testid={`edit-user-btn-${user.email}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        )}
                        {canEdit("users") && user.auth_provider === "local" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPasswordModal(user)}
                            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                            title="Cambiar contraseña"
                            data-testid={`reset-password-btn-${user.email}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete("users") && user.id !== currentUser?.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            disabled={deleting === user.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-user-btn-${user.email}`}
                          >
                            {deleting === user.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-400 border-t-transparent" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        {canEdit("users") && (
                          <Switch
                            checked={user.is_active !== false}
                            onCheckedChange={() => handleToggleActive(user.id, user.is_active)}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Eliminar Usuario
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Estás seguro de eliminar al usuario <span className="text-white font-semibold">"{userToDelete?.name}"</span>? 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border-[#1E1E5F] text-slate-300 hover:bg-[#1B1B4B] hover:text-white"
              data-testid="cancel-delete-btn"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteUser}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
